/**
 * Друзья и таблица среди них (ТЗ FR-GM-07, FR-LB-01).
 *
 * Связывает двоих код приглашения, а не поиск по адресу: сервер намеренно не
 * отвечает, зарегистрирован ли адрес (ТЗ SEC-11), и поиск по нему превратил
 * бы рейтинг в справочник существующих аккаунтов.
 *
 * Свой код показывается целиком — его передают из рук в руки. Чужой не
 * показывается никогда, даже другу: переданный дальше, он приведёт к человеку
 * того, кого он не звал.
 */

import { useState } from 'react';
import type { Translate } from '@type2x/i18n';
import type { AddFriendOutcome, LeaderboardBoard } from '@type2x/feature-leaderboard';
import { Button, EmptyState } from '@type2x/ui';

import type { FriendsAccess } from '../state/friends.js';
import { BoardTable } from './BoardTable.js';

export interface FriendsBlockProps {
  readonly t: Translate;
  readonly friends: FriendsAccess;
  /** Код, с которым открыли приложение по ссылке; `null` — обычный заход. */
  readonly invite: string | null;
}

/**
 * Ссылка-приглашение.
 *
 * Код стоит после решётки: эту часть адреса браузер серверу не отправляет, и
 * она не попадает ни в журналы обращений, ни в `Referer`.
 */
function inviteLink(code: string): string {
  const origin = typeof window === 'undefined' ? '' : window.location.origin;
  return `${origin}/#friend=${encodeURIComponent(code)}`;
}

export function FriendsBlock({ t, friends, invite }: FriendsBlockProps): React.JSX.Element {
  const [code, setCode] = useState('');
  const [outcome, setOutcome] = useState<AddFriendOutcome | null>(null);
  const [busy, setBusy] = useState(false);
  const { view } = friends;

  if (view === null) {
    return (
      <section className="t2x-friends">
        <h3>{t('friends.title')}</h3>
        <p className="t2x-muted">{t('friends.unavailable')}</p>
      </section>
    );
  }

  async function submit(): Promise<void> {
    setBusy(true);
    setOutcome(await friends.add(code.trim()));
    setBusy(false);
    setCode('');
  }

  return (
    <section className="t2x-friends">
      <h3>{t('friends.title')}</h3>
      <p className="t2x-muted t2x-legal">{t('friends.explain')}</p>

      {invite !== null && (
        <Invitation t={t} code={invite} onAccept={() => void friends.add(invite)} />
      )}

      <p className="t2x-friends__code">
        <span className="t2x-muted">{t('friends.yourCode')}</span>{' '}
        <strong className="t2x-friends__value">{view.code}</strong>
      </p>

      {/* Ссылку можно скопировать целиком: переписывать код руками нужно не
          всем и не всегда. Само поле остаётся на месте — там, где буфер
          обмена недоступен, ссылка видна и выделяется. */}
      <label className="t2x-field">
        <span>{t('friends.link')}</span>
        <input type="text" readOnly value={inviteLink(view.code)} />
      </label>

      <label className="t2x-field">
        <span>{t('friends.addLabel')}</span>
        <input
          type="text"
          maxLength={16}
          value={code}
          onChange={(event) => setCode(event.target.value)}
        />
      </label>
      <Button
        variant="secondary"
        disabled={busy || code.trim() === ''}
        onClick={() => void submit()}
      >
        {t('friends.add')}
      </Button>

      {outcome !== null && outcome !== 'added' && (
        <p role="alert" className="t2x-error">
          {t(`friends.error.${outcome}`)}
        </p>
      )}

      <FriendList t={t} friends={friends} />
      <FriendsBoard t={t} board={friends.board} />
    </section>
  );
}

/** Список друзей: псевдоним, участие и кнопка убрать. */
function FriendList({
  t,
  friends,
}: {
  readonly t: Translate;
  readonly friends: FriendsAccess;
}): React.JSX.Element | null {
  if (friends.view === null) return null;

  if (friends.view.friends.length === 0) {
    return <EmptyState title={t('friends.emptyTitle')} body={t('friends.empty')} />;
  }

  return (
    <ul className="t2x-friends__list">
      {friends.view.friends.map((friend) => (
        <li key={friend.linkId} className="t2x-friends__item">
          <span>{friend.displayName}</span>
          {/* Друг без согласия остаётся в списке, но в таблице его нет: чужое
              согласие мы за него не даём (ТЗ FR-LB-06). */}
          {!friend.participating && (
            <span className="t2x-muted t2x-legal">{t('friends.notParticipating')}</span>
          )}
          <Button variant="ghost" onClick={() => void friends.remove(friend.linkId)}>
            {t('friends.remove')}
          </Button>
        </li>
      ))}
    </ul>
  );
}

/** Таблица среди друзей: та же разметка, что у общей. */
function FriendsBoard({
  t,
  board,
}: {
  readonly t: Translate;
  readonly board: LeaderboardBoard | null;
}): React.JSX.Element | null {
  if (board === null || board.rows.length === 0) return null;

  return <BoardTable t={t} board={board} title={t('friends.boardTitle')} />;
}

/**
 * Приглашение, с которым пришли по ссылке.
 *
 * Ссылка не добавляет в друзья сама: она показывает, кто зовёт, и ждёт
 * согласия. Молчаливое добавление по переходу — это не приглашение, а
 * подписка без спроса (ТЗ FR-GM-09).
 */
function Invitation({
  t,
  code,
  onAccept,
}: {
  readonly t: Translate;
  readonly code: string;
  readonly onAccept: () => void;
}): React.JSX.Element | null {
  const [answered, setAnswered] = useState(false);

  if (answered) return null;

  return (
    <aside className="t2x-keyboard-note" role="note">
      <p className="t2x-keyboard-note__title">{t('friends.inviteTitle')}</p>
      <p>{t('friends.invite', { code })}</p>
      <div className="t2x-actions">
        <Button
          variant="secondary"
          onClick={() => {
            onAccept();
            setAnswered(true);
          }}
        >
          {t('friends.inviteAccept')}
        </Button>
        <Button variant="ghost" onClick={() => setAnswered(true)}>
          {t('friends.inviteDecline')}
        </Button>
      </div>
    </aside>
  );
}
